import { Modal } from 'core/components/ui/Modal'
import React from 'react'
import { KycListItem } from '../../domain/entities/Kyc'
import { useKycSensitiveDetail } from '../hooks/useKycSensitiveDetail'
import { ShieldIcon } from 'core/components/Icons'

interface KycDetailModalProps {
  isOpen: boolean
  onClose: () => void
  record: KycListItem | null
  onApprove?: () => void
  onReject?: () => void
  readOnly?: boolean
}

function DocumentTile({
  label,
  url,
  loading,
  hasDoc,
}: {
  label: string
  url: string | null
  loading: boolean
  hasDoc: boolean
}) {
  let body: React.ReactNode
  if (loading) {
    body = <span className="text-xs text-text-secondary">Loading…</span>
  } else if (!hasDoc) {
    body = <span className="text-xs text-text-secondary italic">Not uploaded</span>
  } else if (url) {
    body = (
      <img
        src={url}
        alt={label}
        className="max-h-40 w-full object-cover rounded hover:scale-105 transition-transform duration-200"
      />
    )
  } else {
    body = <span className="text-xs text-danger">Failed to load</span>
  }

  return (
    <div className="border border-border rounded-card overflow-hidden bg-bg/30 flex flex-col justify-between">
      <div className="bg-bg border-b border-border px-3 py-1.5 flex justify-between items-center text-xs text-text-secondary font-semibold">
        <span>{label}</span>
        {url && (
          <a href={url} target="_blank" rel="noreferrer" className="text-primary hover:underline">
            Open Original
          </a>
        )}
      </div>
      <div className="p-2 bg-surface flex-1 flex items-center justify-center min-h-[9rem]">{body}</div>
    </div>
  )
}

function ContactDetailsColumn({
  sensitive,
  loading,
}: {
  sensitive: { name?: string; phone?: string; email?: string; address?: string } | null
  loading: boolean
}) {
  const nameDisplay = loading ? 'Loading…' : sensitive?.name || '—'
  const phoneDisplay = loading ? 'Loading…' : sensitive?.phone || '—'
  const emailDisplay = loading ? 'Loading…' : sensitive?.email || '—'
  const addressDisplay = loading ? 'Loading…' : sensitive?.address || '—'

  return (
    <div className="space-y-1 min-w-0">
      <span className="text-xs font-bold text-text-secondary uppercase tracking-wider block">Contact Person</span>
      <span className="text-sm font-semibold text-text-primary block truncate">{nameDisplay}</span>
      <span className="text-xs text-text-secondary block font-medium">{phoneDisplay}</span>
      <span className="text-xs text-text-secondary block font-medium break-all">{emailDisplay}</span>
      <span className="text-xs text-text-secondary block font-medium break-words">{addressDisplay}</span>
    </div>
  )
}

function BusinessProfileColumn({
  record,
  sensitive,
  loading,
}: {
  record: KycListItem
  sensitive: { pan?: string; gstin?: string } | null
  loading: boolean
}) {
  const panDisplay = loading ? 'Loading…' : sensitive?.pan || '—'
  const gstinDisplay = loading ? 'Loading…' : sensitive?.gstin || '—'

  return (
    <div className="space-y-1 min-w-0">
      <span className="text-xs font-bold text-text-secondary uppercase tracking-wider block">Business Profile</span>
      <span className="text-sm font-semibold text-text-primary block">{record.businessName}</span>
      <span className="text-xs text-text-secondary block">
        <strong>PAN:</strong> <span className="font-mono">{panDisplay}</span>
      </span>
      <span className="text-xs text-text-secondary block">
        <strong>GSTIN:</strong> <span className="font-mono">{gstinDisplay}</span>
      </span>
      <span className="text-xs text-text-secondary block">
        <strong>User ID:</strong> {record.userId}
      </span>
    </div>
  )
}

function ConsentStatusCard({ consentGivenAt }: { consentGivenAt: string | undefined }) {
  if (!consentGivenAt) {
    return (
      <div className="bg-bg border border-border p-3 rounded-btn flex items-center justify-center">
        <span className="text-xs text-text-secondary italic">No consent on record</span>
      </div>
    )
  }

  return (
    <div className="border border-status-active p-3 rounded-btn space-y-1">
      <div className="flex items-center gap-1.5 text-xs font-bold text-status-active uppercase tracking-wider">
        <ShieldIcon className="h-3.5 w-3.5 text-status-active" />
        Consent Accepted
      </div>
      <p className="text-[10px] text-text-secondary leading-snug">
        We may use profile and document details to verify registration and facilitate cattle feed orders.
      </p>
      <span className="text-[9px] text-text-secondary block font-mono">
        Accepted: {new Date(consentGivenAt).toLocaleDateString()}
      </span>
    </div>
  )
}

export function KycDetailModal({
  isOpen,
  onClose,
  record,
  onApprove,
  onReject,
  readOnly = false,
}: KycDetailModalProps) {
  const {
    data: sensitive,
    loading: sensitiveLoading,
    error: sensitiveError,
  } = useKycSensitiveDetail({
    kycId: isOpen ? (record?.id ?? null) : null,
    hasPanDoc: record?.hasPanDoc ?? false,
    hasGstinDoc: record?.hasGstinDoc ?? false,
  })

  if (!record) return null

  const footerActions = !readOnly && (
    <>
      <button
        onClick={onReject}
        className="px-4 py-2 text-sm font-semibold border border-danger/30 hover:border-danger text-danger rounded-btn bg-surface hover:bg-danger/[0.04] transition-colors"
      >
        Reject KYC
      </button>
      <button
        onClick={onApprove}
        className="px-4 py-2 text-sm font-semibold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn transition-colors shadow animate-pulse hover:animate-none"
      >
        Approve & Verify
      </button>
    </>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Review Partner KYC - ${record.businessName}`}
      footer={footerActions || undefined}
      size="lg"
    >
      <div className="space-y-6">
        {sensitiveError && (
          <div className="p-3 text-xs bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium">
            {sensitiveError}
          </div>
        )}

        {/* Verification meta */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 bg-bg/50 border border-border p-4 rounded-card">
          <ContactDetailsColumn sensitive={sensitive} loading={sensitiveLoading} />
          <BusinessProfileColumn record={record} sensitive={sensitive} loading={sensitiveLoading} />
          <ConsentStatusCard consentGivenAt={record.consentGivenAt} />
        </div>

        {/* Uploaded documents images */}
        <div className="space-y-4">
          <h4 className="text-xs font-bold text-text-primary uppercase tracking-wider border-b border-border pb-1">
            KYC Document Verification
          </h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <DocumentTile
              label="PAN Card Copy"
              url={sensitive?.panDocUrl ?? null}
              loading={sensitiveLoading}
              hasDoc={record.hasPanDoc}
            />
            <DocumentTile
              label="GST Certificate"
              url={sensitive?.gstinDocUrl ?? null}
              loading={sensitiveLoading}
              hasDoc={record.hasGstinDoc}
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}
